import React from 'react';
import { Progress, Icon, Button, Tooltip } from 'antd';
import { useJobProgress } from '~/context/JobProgressContext';

var JOB_TYPE_LABELS = {
  monthly_payments_print: 'Gerando PDFs de mensalidades',
};

var JOB_TYPE_ICONS = {
  monthly_payments_print: 'file-pdf',
};

var getLabel = function(job) {
  return JOB_TYPE_LABELS[job.job_type] || job.job_type;
};

var getJobIcon = function(job) {
  return JOB_TYPE_ICONS[job.job_type] || 'file';
};

var getPercent = function(job) {
  if (!job.total_items || job.total_items === 0) return 0;
  return Math.round((job.processed_items / job.total_items) * 100);
};

// Inject keyframes once
var styleInjected = false;
function injectStyles() {
  if (styleInjected) return;
  styleInjected = true;
  var css = [
    '@keyframes jpb-slide-in { from { opacity: 0; transform: translateY(16px) scale(0.96); } to { opacity: 1; transform: translateY(0) scale(1); } }',
    '@keyframes jpb-slide-out { from { opacity: 1; transform: translateY(0) scale(1); } to { opacity: 0; transform: translateY(16px) scale(0.96); } }',
    '@keyframes jpb-pulse { 0%, 100% { opacity: 1; } 50% { opacity: 0.6; } }',
    '@keyframes jpb-progress-stripe { 0% { background-position: 0 0; } 100% { background-position: 24px 0; } }',
    '.jpb-cancel-btn { opacity: 0.7; transition: opacity 0.15s, color 0.15s; }',
    '.jpb-cancel-btn:hover { opacity: 1; color: #ff4d4f !important; }',
    '.jpb-download-btn { transition: background 0.15s, box-shadow 0.15s; }',
    '.jpb-download-btn:hover { background: #389e0d !important; box-shadow: 0 2px 8px rgba(82,196,26,0.4) !important; }',
  ].join('\n');
  var style = document.createElement('style');
  style.textContent = css;
  document.head.appendChild(style);
}

var STATUS_CONFIG = {
  queued: {
    accentColor: '#1890ff',
    bgGradient: 'linear-gradient(135deg, #f0f7ff 0%, #e6f0ff 100%)',
    borderColor: 'rgba(24,144,255,0.15)',
    iconType: 'clock-circle',
    iconSpin: true,
    statusLabel: 'Na fila',
    statusColor: '#1890ff',
  },
  processing: {
    accentColor: '#1890ff',
    bgGradient: 'linear-gradient(135deg, #f0f7ff 0%, #e6f0ff 100%)',
    borderColor: 'rgba(24,144,255,0.15)',
    iconType: 'loading',
    iconSpin: true,
    statusLabel: 'Processando',
    statusColor: '#1890ff',
  },
  completed: {
    accentColor: '#52c41a',
    bgGradient: 'linear-gradient(135deg, #f6ffed 0%, #edf8e6 100%)',
    borderColor: 'rgba(82,196,26,0.15)',
    iconType: 'check-circle',
    iconSpin: false,
    statusLabel: 'Concluido',
    statusColor: '#52c41a',
  },
  failed: {
    accentColor: '#ff4d4f',
    bgGradient: 'linear-gradient(135deg, #fff2f0 0%, #ffeded 100%)',
    borderColor: 'rgba(255,77,79,0.15)',
    iconType: 'close-circle',
    iconSpin: false,
    statusLabel: 'Erro',
    statusColor: '#ff4d4f',
  },
  cancelled: {
    accentColor: '#8c8c8c',
    bgGradient: 'linear-gradient(135deg, #fafafa 0%, #f5f5f5 100%)',
    borderColor: 'rgba(140,140,140,0.15)',
    iconType: 'stop',
    iconSpin: false,
    statusLabel: 'Cancelado',
    statusColor: '#8c8c8c',
  },
};

var JobProgressItem = function(props) {
  var job = props.job;
  var onDismiss = props.onDismiss;
  var onCancel = props.onCancel;
  var status = job.status;
  var percent = getPercent(job);
  var isDismissing = job._dismissing;

  var cfg = STATUS_CONFIG[status] || STATUS_CONFIG.processing;
  var isActive = status === 'queued' || status === 'processing';
  var isCompleted = status === 'completed';
  var isFailed = status === 'failed';
  var isCancelled = status === 'cancelled';
  var isDone = isCompleted || isFailed || isCancelled;

  var containerStyle = {
    background: cfg.bgGradient,
    border: '1px solid ' + cfg.borderColor,
    borderRadius: '12px',
    padding: '14px 18px',
    marginBottom: '8px',
    boxShadow: '0 4px 24px rgba(0,0,0,0.08), 0 1px 4px rgba(0,0,0,0.04)',
    animation: isDismissing ? 'jpb-slide-out 0.3s ease forwards' : 'jpb-slide-in 0.3s ease',
    position: 'relative',
    overflow: 'hidden',
    minWidth: '340px',
    maxWidth: '400px',
  };

  // Accent bar on the left
  var accentBarStyle = {
    position: 'absolute',
    left: 0,
    top: 0,
    bottom: 0,
    width: '3px',
    background: cfg.accentColor,
    borderRadius: '12px 0 0 12px',
  };

  var headerStyle = {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: isActive ? '10px' : '0',
  };

  var titleGroupStyle = {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    minWidth: 0,
  };

  var iconWrapStyle = {
    width: '28px',
    height: '28px',
    borderRadius: '8px',
    background: cfg.accentColor + '14',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
  };

  var titleStyle = {
    fontSize: '13px',
    fontWeight: 600,
    color: '#262626',
    lineHeight: '18px',
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
  };

  var statusBadgeStyle = {
    display: 'inline-flex',
    alignItems: 'center',
    gap: '4px',
    fontSize: '11px',
    fontWeight: 500,
    color: cfg.statusColor,
    animation: isActive ? 'jpb-pulse 2s ease-in-out infinite' : 'none',
  };

  var progressBarWrapperStyle = {
    marginBottom: '6px',
  };

  var footerStyle = {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
  };

  var countTextStyle = {
    fontSize: '11px',
    color: '#8c8c8c',
    fontWeight: 400,
  };

  var actionsGroupStyle = {
    display: 'flex',
    alignItems: 'center',
    gap: '6px',
  };

  // Status text
  var statusText = cfg.statusLabel;
  if (job.message && (isActive || isFailed)) {
    statusText = job.message;
  }

  // Count text
  var countText = '';
  if (isActive && job.total_items) {
    countText = (job.processed_items || 0) + ' de ' + job.total_items;
  } else if (isCompleted && job.total_items) {
    countText = job.total_items + ' arquivo(s)';
  }

  return React.createElement('div', { style: containerStyle },
    // Accent bar
    React.createElement('div', { style: accentBarStyle }),

    // Header row: icon + title + status
    React.createElement('div', { style: headerStyle },
      React.createElement('div', { style: titleGroupStyle },
        React.createElement('div', { style: iconWrapStyle },
          React.createElement(Icon, {
            type: getJobIcon(job),
            style: { color: cfg.accentColor, fontSize: '14px' }
          })
        ),
        React.createElement('div', null,
          React.createElement('div', { style: titleStyle }, getLabel(job)),
          React.createElement('span', { style: statusBadgeStyle },
            React.createElement(Icon, {
              type: cfg.iconType,
              spin: cfg.iconSpin,
              style: { fontSize: '10px' }
            }),
            statusText
          )
        )
      ),

      // Close / cancel button
      React.createElement('div', { style: { flexShrink: 0, marginLeft: '8px' } },
        isActive
          ? React.createElement(Tooltip, { title: 'Cancelar' },
              React.createElement(Button, {
                className: 'jpb-cancel-btn',
                type: 'link',
                size: 'small',
                onClick: function() { onCancel(job.id); },
                style: { color: '#8c8c8c', padding: 0, width: '24px', height: '24px', lineHeight: '24px' }
              },
                React.createElement(Icon, { type: 'close', style: { fontSize: '12px' } })
              )
            )
          : isDone
            ? React.createElement(Button, {
                type: 'link',
                size: 'small',
                onClick: function() { onDismiss(job.id); },
                style: { color: '#bfbfbf', padding: 0, width: '24px', height: '24px', lineHeight: '24px' }
              },
                React.createElement(Icon, { type: 'close', style: { fontSize: '12px' } })
              )
            : null
      )
    ),

    // Progress bar (only for active states)
    isActive && React.createElement('div', { style: progressBarWrapperStyle },
      React.createElement(Progress, {
        percent: status === 'queued' ? 0 : percent,
        status: 'active',
        strokeColor: {
          '0%': cfg.accentColor,
          '100%': cfg.accentColor === '#1890ff' ? '#40a9ff' : cfg.accentColor,
        },
        strokeWidth: 6,
        showInfo: false,
      })
    ),

    // Footer: count + actions
    isActive && React.createElement('div', { style: footerStyle },
      React.createElement('span', { style: countTextStyle },
        countText || (status === 'queued' ? 'Aguardando...' : '')
      ),
      percent > 0 && React.createElement('span', { style: { fontSize: '11px', fontWeight: 600, color: cfg.accentColor } },
        percent + '%'
      )
    ),

    // Completed actions row
    isCompleted && React.createElement('div', { style: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginTop: '10px' } },
      React.createElement('span', { style: countTextStyle }, countText),
      job.result_url && React.createElement(
        'a',
        {
          className: 'jpb-download-btn',
          href: job.result_url,
          target: '_blank',
          rel: 'noopener noreferrer',
          style: {
            display: 'inline-flex',
            alignItems: 'center',
            gap: '4px',
            background: '#52c41a',
            color: '#fff',
            padding: '4px 14px',
            borderRadius: '6px',
            fontSize: '12px',
            fontWeight: 600,
            textDecoration: 'none',
            whiteSpace: 'nowrap',
          }
        },
        React.createElement(Icon, { type: 'download', style: { fontSize: '12px' } }),
        'Baixar'
      )
    ),

    // Failed message row
    isFailed && job.message && React.createElement('div', {
      style: { marginTop: '6px', fontSize: '11px', color: '#ff4d4f' }
    }, job.message)
  );
};

var JobProgressBar = function() {
  injectStyles();

  var _ctx = useJobProgress();
  var activeJobs = _ctx.activeJobs;
  var dismissJob = _ctx.dismissJob;
  var cancelJob = _ctx.cancelJob;

  if (!activeJobs || activeJobs.length === 0) return null;

  var wrapperStyle = {
    position: 'fixed',
    bottom: '24px',
    right: '24px',
    zIndex: 1050,
    display: 'flex',
    flexDirection: 'column-reverse',
    gap: '0',
  };

  return React.createElement('div', { style: wrapperStyle },
    activeJobs.map(function(job) {
      return React.createElement(JobProgressItem, {
        key: job.id,
        job: job,
        onDismiss: dismissJob,
        onCancel: cancelJob
      });
    })
  );
};

export default JobProgressBar;
